/**
 * AdhocInterestPaymentSection Component
 * Handles ad-hoc interest payment dates in the contract form.
 * Users specify dates only — the system calculates the accrued interest amount.
 * Read-only for non-Draft contracts (Active/Approved/Matured).
 */

import React, { useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useContractFormContext } from '../ContractFormContext';

const formatAmount = (val) => {
  const num = parseFloat(val);
  if (isNaN(num)) return '';
  const parts = num.toFixed(2).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return parts.join('.');
};

function AmountInput({ value, onChange }) {
  const [focused, setFocused] = useState(false);
  const raw = value != null && value !== '' ? String(value) : '';
  const display = !focused && raw ? formatAmount(raw) : raw;

  return (
    <TextField
      value={display}
      onChange={(e) => {
        const cleaned = e.target.value.replace(/[^\d.]/g, '');
        onChange(cleaned === '' ? null : cleaned);
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      size="small"
      fullWidth
      placeholder="Auto-calculated"
    />
  );
}

function AdhocInterestPaymentSection() {
  const {
    adhocInterestPayments,
    handleAddAdhocInterestPayment,
    handleUpdateAdhocInterestPayment,
    handleRemoveAdhocInterestPayment,
    initialData,
  } = useContractFormContext();

  const isReadOnly = initialData?.status && initialData.status !== 'Draft';

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return dateStr; }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Adhoc Interest Payment Dates
        </Typography>
        {!isReadOnly && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddAdhocInterestPayment}
            size="small"
          >
            Add Payment Date
          </Button>
        )}
      </Box>

      {isReadOnly ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Scheduled interest payment dates on this contract. The amount is calculated automatically by the system.
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add non-regular interest payment dates. On each date, all accrued interest since the
          last payment will be paid out. The amount is calculated automatically by the system.
        </Typography>
      )}

      {adhocInterestPayments.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
          <Typography color="text.secondary">
            {isReadOnly ? 'No adhoc interest payment dates on this contract.' : 'No adhoc interest payment dates. Click "Add Payment Date" to add one.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell width={isReadOnly ? '35%' : '30%'}>Date</TableCell>
                <TableCell width={isReadOnly ? '20%' : '20%'}>Amount</TableCell>
                <TableCell width={isReadOnly ? '30%' : '40%'}>Description</TableCell>
                {isReadOnly && <TableCell width="15%">Source</TableCell>}
                {!isReadOnly && <TableCell width="10%" align="center">Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {adhocInterestPayments.map((event, index) => (
                <TableRow key={event.id || index}>
                  <TableCell>
                    {isReadOnly ? (
                      <Typography variant="body2">{formatDate(event.event_date)}</Typography>
                    ) : (
                      <TextField
                        type="date"
                        value={event.event_date || ''}
                        onChange={(e) => handleUpdateAdhocInterestPayment(index, 'event_date', e.target.value)}
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {isReadOnly ? (
                      <Typography variant="body2">
                        {event.amount != null ? formatAmount(event.amount) : 'Auto'}
                      </Typography>
                    ) : (
                      <AmountInput
                        value={event.amount}
                        onChange={(val) => handleUpdateAdhocInterestPayment(index, 'amount', val)}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {isReadOnly ? (
                      <Typography variant="body2" color="text.secondary">{event.description || 'Adhoc interest payment'}</Typography>
                    ) : (
                      <TextField
                        value={event.description || ''}
                        onChange={(e) => handleUpdateAdhocInterestPayment(index, 'description', e.target.value)}
                        size="small"
                        fullWidth
                        placeholder="Optional note"
                      />
                    )}
                  </TableCell>
                  {isReadOnly && (
                    <TableCell>
                      <Chip
                        label={event.amendment_source === 'amendment' ? 'Amendment' : 'Draft'}
                        size="small"
                        variant="outlined"
                        color={event.amendment_source === 'amendment' ? 'info' : 'default'}
                      />
                    </TableCell>
                  )}
                  {!isReadOnly && (
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveAdhocInterestPayment(index)}
                        title="Remove payment date"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!isReadOnly && adhocInterestPayments.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Note: Events without a date will be ignored when generating the schedule.
        </Typography>
      )}
    </Box>
  );
}

export default AdhocInterestPaymentSection;
